import { PillarsVisualization as PillarsVisualizationType } from '@/types/sanity';
import { Box } from '../box';
import { Text } from '../text';
import { useActiveSectionIndex } from './hooks/use-active-section-index';
import { Canvas } from '@react-three/fiber';
import { keyframes, styled } from '@/stitches.config';
import { PillarsCanvasContent } from './pillars-canvas-content';
import { useExperienceColorsStore } from '@/hooks/useExperienceColorsStore';
import { useHeaderStore } from '@/hooks/useHeaderStore';
import * as Accordion from '@radix-ui/react-accordion';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { Minus, PlusThin } from '../icons';
import { AnimatePresence, motion } from 'framer-motion';
import { Html, OrthographicCamera } from '@react-three/drei';
import { Title } from '../contact/styled-text';
import { useEffect, useRef, useState } from 'react';
import { debounce } from 'debounce';

export function PillarsVisualization({
  title,
  pillars,
}: PillarsVisualizationType) {
  const isWideScreen = useBreakpoint('bp4');
  const isTallScreen = useBreakpoint('heightBp3');
  const isColumnLayout = !(isWideScreen && isTallScreen);

  const {
    activeSectionIndex,
    markersRef,
    jumpToSection,
    setActiveSectionIndex,
  } = useActiveSectionIndex({ isEnabled: !isColumnLayout });

  const experienceColors = useExperienceColorsStore(
    (store) => store.experienceColors
  );

  const headerHeight = useHeaderStore((store) => store.headerHeight);

  const sectionHeightReferenceRef = useRef<HTMLDivElement>(null);
  const visualizationHeightReferenceRef = useRef<HTMLDivElement>(null);
  const [negativeMargin, setNegativeMargin] = useState(0);

  // The visualisation is centered on the screen when it is sticky. It's easier
  // to layout the sticky visualisation when its vertically centered in a sticky
  // 100vh div. But this makes it hard to keep small margins above and below the
  // visualisation (for when it's not sticky anymore). To allow for small
  // margins, the height difference between the visualisation and its 100vh
  // wrapper is applied to the 100vh div as negative margins.
  //
  // Good to note that the height of the visualisation changes when the
  // different sections of the accordion open.
  useEffect(() => {
    const visualizationHeightReference =
      visualizationHeightReferenceRef.current;
    const sectionHeightReference = sectionHeightReferenceRef.current;

    if (
      isColumnLayout ||
      !visualizationHeightReference ||
      !sectionHeightReference
    )
      return;

    const resizeObserverCallback: ResizeObserverCallback = debounce(() => {
      setNegativeMargin(
        // Dividing by two since the extra margin will be removed equally from
        // the top and the bottom
        (sectionHeightReference.getBoundingClientRect().height -
          visualizationHeightReference.getBoundingClientRect().height) /
          2
      );
    }, 100);

    const resizeObserver = new ResizeObserver(resizeObserverCallback);

    resizeObserver.observe(visualizationHeightReference);
    resizeObserver.observe(sectionHeightReference);

    return () => resizeObserver.disconnect();
  }, [isColumnLayout]);

  const canvasContentJsx = (
    <>
      <Html>
        <AnimatePresence mode="wait">
          {activeSectionIndex > 0 && (
            <Text
              key={pillars[activeSectionIndex - 1].title}
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              css={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontWeight: 500,
              }}
              size={3}
            >
              {pillars[activeSectionIndex - 1].title}
            </Text>
          )}
        </AnimatePresence>
      </Html>
      {experienceColors && (
        <PillarsCanvasContent
          visualizationType={
            activeSectionIndex === 0
              ? 'unsortedRing'
              : pillars[activeSectionIndex - 1].visualizationType
          }
          experienceColors={experienceColors}
        />
      )}
      <OrthographicCamera
        position={[0, 0, 100]}
        far={9999}
        zoom={100}
        makeDefault
      />
    </>
  );

  return (
    <Box
      css={{
        $$sectionHeight: `calc(100vh - ${headerHeight}px)`,
        position: 'relative',
        height: isColumnLayout
          ? 'auto'
          : `calc($$sectionHeight * ${pillars.length + 2})`,
      }}
    >
      <Box
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          overflow: 'visible',
        }}
      >
        {!isColumnLayout &&
          new Array(pillars.length + 1).fill(null).map((_, sectionIndex) => (
            <Box
              key={sectionIndex}
              css={{
                top: `calc(${sectionIndex} * $$sectionHeight)`,
                width: '100%',
                height: '$$sectionHeight',
                '&:first-child, &:last-child': {
                  height: 'calc($$sectionHeight * 1.5)',
                },
              }}
              ref={(element) => {
                markersRef.current[sectionIndex] = element;
              }}
            />
          ))}
      </Box>

      <Title
        css={{
          color: '$foreground',
          marginBottom: isColumnLayout ? '$11' : '$8',
        }}
      >
        {title}
      </Title>

      <Box
        css={{
          display: 'flex',
          flexFlow: 'column nowrap',
          position: isColumnLayout ? 'relative' : 'sticky',
          top: isColumnLayout ? undefined : headerHeight,
          width: '100%',
          height: isColumnLayout ? 'auto' : '$$sectionHeight',
          overflow: 'hidden',
          my: isColumnLayout ? undefined : -negativeMargin,
        }}
        ref={sectionHeightReferenceRef}
      >
        <Box
          css={{
            display: 'flex',
            flexFlow: 'row nowrap',
            alignItems: 'center',
            gap: '$12',
            position: isColumnLayout ? 'relative' : 'absolute',
            top: '50%',
            left: 0,
            width: '100%',
            transform: isColumnLayout ? undefined : 'translateY(-50%)',
          }}
          ref={visualizationHeightReferenceRef}
        >
          {!isColumnLayout && (
            <Box
              css={{
                position: 'relative',
                flexGrow: 1,
                aspectRatio: '1 / 1',
                maxHeight: 'min(60rem, 80vh)',
              }}
            >
              <StyledCanvas
                style={{ position: 'absolute' }}
                css={{
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              >
                {canvasContentJsx}
              </StyledCanvas>
            </Box>
          )}
          <Box
            css={{
              width: isColumnLayout ? '100%' : 'min(30rem, 50%)',
            }}
          >
            <StyledAccordionRoot
              type="single"
              value={
                activeSectionIndex === 0
                  ? ''
                  : pillars[activeSectionIndex - 1].title
              }
              onValueChange={(newval) => {
                const pillarIndex = pillars.findIndex(
                  (x) => x.title === newval
                );

                if (pillarIndex < 0) return;

                if (isColumnLayout) {
                  setActiveSectionIndex(pillarIndex + 1);
                } else {
                  jumpToSection(pillarIndex + 1);
                }
              }}
            >
              {pillars.map((pillar, pillarIndex) => (
                <StyledAccordionItem key={pillar.title} value={pillar.title}>
                  <StyledAccordionHeader>
                    <StyledAccordionTrigger
                      css={{
                        width: '100%',
                      }}
                    >
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          position: 'relative',
                          px: '$3',
                          py: '$5',
                          '& svg': {
                            width: 16,
                          },
                        }}
                      >
                        <Text as="h4" size={5} css={{ margin: 0 }}>
                          {pillar.title}
                        </Text>

                        {activeSectionIndex === pillarIndex + 1 ? (
                          <Minus />
                        ) : (
                          <PlusThin />
                        )}
                      </Box>
                    </StyledAccordionTrigger>
                  </StyledAccordionHeader>
                  <StyledAccordionContent>
                    <Box
                      css={{
                        display: 'flex',
                        flexFlow: 'column nowrap',
                        gap: '$8',
                        px: '$3',
                        pb: '$5',
                        '@bp3': {
                          flexFlow: 'row nowrap',
                          alignItems: 'center',
                        },
                      }}
                    >
                      <Text
                        as="p"
                        size={3}
                        css={{
                          marginBottom: '$1',
                          marginTop: '$3',
                          width: '100%',
                          '@bp3': {
                            width: '50%',
                          },
                          '@bp4': {
                            width: '100%',
                          },
                        }}
                      >
                        {pillar.description}
                      </Text>
                      {isColumnLayout && (
                        <Box
                          css={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '4 / 3',
                            '@bp3': {
                              width: '50%',
                            },
                          }}
                        >
                          <Box
                            css={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                            }}
                          >
                            <StyledCanvas>{canvasContentJsx}</StyledCanvas>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </StyledAccordionContent>
                </StyledAccordionItem>
              ))}
            </StyledAccordionRoot>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const openAnimation = keyframes({
  '0%': {
    height: 0,
  },
  '100%': {
    height: 'var(--radix-accordion-content-height)',
  },
});

const closeAnimation = keyframes({
  '0%': {
    height: 'var(--radix-accordion-content-height)',
  },
  '100%': {
    height: 0,
  },
});

const StyledAccordionRoot = styled(Accordion.Root);
const StyledAccordionItem = styled(Accordion.Item, {
  borderBottom: '1px solid $borderColor',
  '&:first-child': {
    borderTop: '1px solid $borderColor',
  },
});
const StyledAccordionHeader = styled(Accordion.Header);
const StyledAccordionTrigger = styled(Accordion.Trigger);
const StyledAccordionContent = styled(Accordion.Content, {
  overflow: 'hidden',
  '&[data-state="closed"]': {
    animation: `${closeAnimation} 500ms`,
  },
  '&[data-state="open"]': {
    animation: `${openAnimation} 500ms`,
  },
  transition: 'height 1s',
});

const StyledCanvas = styled(Canvas);
